import { useRef, useState } from 'react';
import { Button, Col, Row, Spinner } from 'react-bootstrap';
import {
  useGetOrdersQuery,
  useLazyGetOrderByIdQuery,
  useShowRealOrLegalMutation,
} from '@/app/Purchase/api/ordersApi';
import { ordersColumnConfig } from '../config/ordersColumnConfig';
import OrderDetailsModal from '../components/OrderDetailsModal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { breakpointsMediaQuery, useMediaQuery } from '@/helpers/utils';
import ToolsBreadcrumb from '@/app/common/components/ToolsBreadcrumb';
import AgGridWrapper from '@/app/common/components/ag-grid/AgGridWrapper';
import AgGridSkeleton from '@/app/common/components/skeleton/AgGridSkeleton';
import LegalModalContainer from '@/app/Purchase/components/LegalModal';
import IconAlert from '@/components/common/IconAlert';
import timeStampToDate from '@/helpers/timeStampToDate';
import type { OrderListItem } from '../types/ordersApiTypes';

function Orders() {
  const isMdUp = useMediaQuery(breakpointsMediaQuery.md);
  const [showInvoice, setShowInvoice] = useState(false);
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);
  const gridRef = useRef<HTMLDivElement>(null);
  const { data: ordersList, isLoading: isLoadingOrders } = useGetOrdersQuery();
  const [showRealOrLegal, { data: legalData, isLoading: isLoadingLegalData }] =
    useShowRealOrLegalMutation();
  const [
    fetchOrderById,
    { data: orderDetailsResponse, isFetching: isLoadingOrderDetails, isError: isOrderDetailsError },
  ] = useLazyGetOrderByIdQuery();

  function handleViewOrderDetails(orderId: string) {
    setSelectedOrderId(orderId);
    fetchOrderById({ order_id: orderId });
  }

  function handleCloseOrderDetails() {
    setSelectedOrderId(null);
  }

  return (
    <>
      <ToolsBreadcrumb
        parentName="سفارشات"
        parentUrl={`/orders/`}
        childs={[]}
      />
      <LegalModalContainer
        init={legalData?.data}
        setShow={setShowInvoice}
        show={showInvoice}
      />
      <OrderDetailsModal
        show={Boolean(selectedOrderId)}
        onHide={handleCloseOrderDetails}
        order={
          orderDetailsResponse?.success === false
            ? undefined
            : orderDetailsResponse?.data
        }
        isLoading={isLoadingOrderDetails}
        isError={
          isOrderDetailsError || orderDetailsResponse?.success === false
        }
        errorMessage={orderDetailsResponse?.msg}
      />
      <Row>
        <Row className="gap-2 m-0 justify-content-start mb-2">
          <Col md="auto" xs={12} className="p-0 ">
            <Button
              className="py-1 w-100"
              variant="falcon-primary"
              size="sm"
              disabled={isLoadingLegalData}
              onClick={() => {
                showRealOrLegal()
                  .unwrap()
                  .then(() => {
                    setShowInvoice(true);
                  });
              }}
            >
              {isLoadingLegalData ? (
                <span>
                  <Spinner
                    style={{
                      width: '15px',
                      height: '15px'
                    }}
                    className="me-1"
                  />
                  در حال دریافت اطلاعات
                </span>
              ) : (
                <span>
                  {' '}
                  <FontAwesomeIcon icon="file-invoice" className="me-1" />{' '}
                  اطلاعات فاکتور{' '}
                </span>
              )}
            </Button>
          </Col>
          <Col md="auto" xs={12} className="p-0">
            <Button
              as="a"
              className="py-1 w-100"
              href="https://mizfa.tools/docs/finance/invoice/"
              target="_blank"
              rel="noopener noreferrer"
              variant="secondary"
              size="sm"
            >
              <FontAwesomeIcon icon="question-circle" className="me-1" />
              دریافت فاکتور و پیش‌فاکتور
            </Button>
          </Col>
        </Row>
        {ordersList?.data?.factor_status !== null && (
          <Col xxl={12}>
            {ordersList?.data?.factor_status === 0 && (
              <IconAlert variant="warning" className="mb-2">
                اطلاعات مالی شما در حال بررسی است.
              </IconAlert>
            )}

            {ordersList?.data?.factor_status === 1 && (
              <IconAlert variant="success" className="mb-2">
                اطلاعات مالی شما در تاریخ  <span style={{unicodeBidi: 'plaintext'}}>{timeStampToDate(Number(ordersList?.data?.real_and_legal_updated_at)).slice(8).toPersianDigit()}</span>  تایید شد. از زمان تایید، فاکتورهای جدید فقط بر
                اساس این مشخصات صادر و تا آخر ماه در سامانه مودیان
                ثبت خواهند شد. در نظر داشته باشید امکان صدور یا ویرایش فاکتورهای
                قبلی وجود ندارد.
              </IconAlert>
            )}

            {ordersList?.data?.factor_status === 2 && (
              <IconAlert variant="danger" className="mb-2">
                متاسفانه اطلاعات مالی شما به علت{' '}
                <b> {ordersList.data?.rejection_reason} </b> تایید نشد. لطفا از
                صحت داده‌ها اطمینان حاصل کنید و اطلاعات صحیح را دوباره وارد کنید
                تا بررسی شود.
              </IconAlert>
            )}
          </Col>
        )}
        <Col xxl={12}>
          <IconAlert variant="warning" className="mb-2">
            کلیه پیامدهای ناشی از عدم مطالعه این{' '}
            <a
              className="alert-link"
              href="https://help.mizfa.tools/factor/"
              target="_blank"
              rel="noreferrer noopener"
            >
              راهنما
            </a>{' '}
            یا عدم رعایت موارد یادشده در زمینه فاکتور (و سامانه مودیان)، بر عهده
            خریدار خواهد بود.
          </IconAlert>
        </Col>
        {
          <Col xxl={12} className="mb-2">
            {doesAnyTaxIdExist(ordersList?.data?.orders ?? []) && (
              <IconAlert className="mb-0" variant="warning">
                شماره مالیاتی جایگزین فاکتور های قدیمی (دارای مهر و امضا)
                شده‌اند. لطفا شماره مالیاتی سفارش خود را جهت استفاده در
                هزینه‌ها به بخش مالی شرکت خود ارسال کنید.
              </IconAlert>
            )}
          </Col>
        }
        <Col xxl={12}>
          <div ref={gridRef}>
            {!isLoadingOrders ? (
              <AgGridWrapper
                height="700px"
                data={ordersList?.data?.orders ? ordersList.data?.orders : []}
                columns={ordersColumnConfig(
                  setShowInvoice,
                  isMdUp,
                  doesAnyTaxIdExist(ordersList?.data?.orders ?? []),
                  ordersList?.data?.factor_status,
                  ordersList?.data?.real_and_legal_updated_at,
                  handleViewOrderDetails,
                )}
              />
            ) : (
              <AgGridSkeleton height="550px" />
            )}
          </div>
        </Col>
      </Row>
    </>
  );
};

export default Orders;

const doesAnyTaxIdExist = (data: OrderListItem[]) =>
  data.some((item) => item.tax_id !== null && item.tax_id !== undefined);
